<div>
  <div class="container-fluid">
    <!-- Page Header -->
    <div class="row mb-4">
      <div class="col">
        <h1 class="h2 mb-1 fw-bold">My Profile</h1>
        <p class="text-muted">View your account information</p>
      </div>
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Error Message -->
    <div *ngIf="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''" aria-label="Close"></button>
    </div>

    <!-- Profile Content -->
    <div *ngIf="!loading && profile" class="row">
      <!-- Profile Information -->
      <div class="col-md-6 mb-4">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">
              <i class="bi bi-person-circle me-2"></i>
              Profile Information
            </h5>
          </div>
          <div class="card-body">
            <!-- Avatar -->
            <div *ngIf="profile.avatarUrl" class="text-center mb-3">
              <img [src]="profile.avatarUrl" alt="Profile Avatar" class="rounded-circle" width="80" height="80">
            </div>
            <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 py-2 border-bottom">
              <label class="fw-medium text-body-secondary m-0">First Name</label>
              <div class="flex-grow-1 text-md-end text-break">{{ profile.firstName || 'N/A' }}</div>
            </div>
            <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 py-2 border-bottom">
              <label class="fw-medium text-body-secondary m-0">Last Name</label>
              <div class="flex-grow-1 text-md-end text-break">{{ profile.lastName || 'N/A' }}</div>
            </div>
            <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 py-2 border-bottom">
              <label class="fw-medium text-body-secondary m-0">Email</label>
              <div class="flex-grow-1 text-md-end text-break">{{ profile.email }}</div>
            </div>
            <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 py-2 border-bottom">
              <label class="fw-medium text-body-secondary m-0">Phone</label>
              <div class="flex-grow-1 text-md-end text-break">{{ (profile.phone | phoneFormat) || 'N/A' }}</div>
            </div>
            <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 py-2 border-bottom">
              <label class="fw-medium text-body-secondary m-0">Date of Birth</label>
              <div class="flex-grow-1 text-md-end text-break">{{ profile.dateOfBirth ? formatDate(profile.dateOfBirth) : 'N/A' }}</div>
            </div>
            <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 py-2">
              <label class="fw-medium text-body-secondary m-0">Username</label>
              <div class="flex-grow-1 text-md-end text-break">{{ profile.username || 'N/A' }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Address -->
      <div class="col-md-6 mb-4">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">
              <i class="bi bi-geo-alt me-2"></i>
              Address
            </h5>
          </div>
          <div class="card-body">
            <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 py-2 border-bottom">
              <label class="fw-medium text-body-secondary m-0">Street Address</label>
              <div class="flex-grow-1 text-md-end text-break">{{ profile.streetAddressLine1 || 'N/A' }}</div>
            </div>
            <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 py-2 border-bottom">
              <label class="fw-medium text-body-secondary m-0">City</label>
              <div class="flex-grow-1 text-md-end text-break">{{ profile.city || 'N/A' }}</div>
            </div>
            <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 py-2 border-bottom">
              <label class="fw-medium text-body-secondary m-0">State</label>
              <div class="flex-grow-1 text-md-end text-break">{{ profile.state || 'N/A' }}</div>
            </div>
            <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 py-2">
              <label class="fw-medium text-body-secondary m-0">Zip Code</label>
              <div class="flex-grow-1 text-md-end text-break">{{ profile.zipCode || 'N/A' }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Account Details -->
      <div class="col-md-6 mb-4">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">
              <i class="bi bi-shield-lock me-2"></i>
              Account Details
            </h5>
          </div>
          <div class="card-body">
            <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 py-2 border-bottom">
              <label class="fw-medium text-body-secondary m-0">User ID</label>
              <div class="flex-grow-1 text-md-end text-break"><code>{{ profile.id }}</code></div>
            </div>
            <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 py-2 border-bottom">
              <label class="fw-medium text-body-secondary m-0">Email Verified</label>
              <div class="flex-grow-1 text-md-end text-break">
                <span class="badge" [ngClass]="profile.emailVerified ? 'bg-success' : 'bg-warning text-dark'">
                  {{ profile.emailVerified ? 'Verified' : 'Unverified' }}
                </span>
              </div>
            </div>
            <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 py-2 border-bottom">
              <label class="fw-medium text-body-secondary m-0">Two-Factor Auth</label>
              <div class="flex-grow-1 text-md-end text-break">
                <span class="badge" [ngClass]="profile.is2faEnabled ? 'bg-success' : 'bg-secondary'">
                  {{ profile.is2faEnabled ? 'Enabled' : 'Disabled' }}
                </span>
              </div>
            </div>
            <div *ngIf="profile.is2faEnabled" class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 py-2 border-bottom">
              <label class="fw-medium text-body-secondary m-0">2FA Method</label>
              <div class="flex-grow-1 text-md-end text-break">{{ get2FAMethodLabel() }}</div>
            </div>
            <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 py-2 border-bottom">
              <label class="fw-medium text-body-secondary m-0">Onboarding Status</label>
              <div class="flex-grow-1 text-md-end text-break">{{ profile.onboardingStatus || 'N/A' }}</div>
            </div>
            <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 py-2 border-bottom">
              <label class="fw-medium text-body-secondary m-0">Language</label>
              <div class="flex-grow-1 text-md-end text-break">{{ profile.language || 'N/A' }}</div>
            </div>
            <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 py-2">
              <label class="fw-medium text-body-secondary m-0">Timezone</label>
              <div class="flex-grow-1 text-md-end text-break">{{ profile.timezone || 'N/A' }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
